import React, { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import axios from 'axios';
import {
  useToast,
  Button,
  Flex,
  Text,
  Box,
  VStack,
  HStack,
  Progress,
  Spinner,
  Checkbox,
} from '@chakra-ui/react';
import InputField from 'components/formComponents/inputField';
import SelectField from 'components/formComponents/selectField';
import ActiveStoresApi from 'api/activestores';
import CustomerSavingPlanApi from 'api/customersavingplan';
import { useHistory } from 'react-router-dom';
import Card from 'components/card/Card';
import { Categories } from 'config/constant';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import MultiLineSelectField from 'components/formComponents/multiLineSelectField';
import * as Yup from 'yup';
import { OrderSummaryPage } from './OrderSummary';

// Validation schema for Step 1
const step1Schema = Yup.object().shape({
  plan: Yup.string().required('Plan is required'),
  variant: Yup.string().required('Variant is required'),
  store: Yup.string().required('Branch is required'),
});

// Validation schema for Step 2
const step2Schema = Yup.object().shape({
  nomineeName: Yup.string().required('Nominee name is required'),
  nomineeRelation: Yup.string().required('Nominee relation is required'),
  nomineeContact: Yup.string()
    .matches(/^\d{10}$/, 'Nominee contact must be a 10-digit number')
    .required('Nominee contact is required'),
  nomineeDob: Yup.date().required('Nominee date of birth is required'),
  nomineeAadhaar: Yup.string()
    .matches(/^\d{12}$/, 'Aadhaar number must be a 12-digit number')
    .nullable(),
});

// Combine schemas for multi-step form
const validationSchema = Yup.object().shape({
  step1: step1Schema,
  step2: step2Schema,
});

const StoreCard = ({ store, isSelected, onClick }) => (
  <Card
    borderWidth={isSelected ? '2px' : '1px'}
    borderColor={isSelected ? 'blue.500' : 'gray.200'}
    p={4}
    cursor='pointer'
    onClick={onClick}
  >
    <Text fontWeight='bold'>{store.storename}</Text>
    <Text>{store.address_line1}</Text>
    <Text>
      {store.city}, {store.state}, {store.country}
    </Text>
    <Text>{store.pincode}</Text>
  </Card>
);

const Step1 = ({ values, setFieldValue }) => {
  const [variants, setVariants] = useState([]);
  const [stores, setStores] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [errorVariants, setErrorVariants] = useState('');
  const [errorStores, setErrorStores] = useState('');

  const [minInvestment, setMinInvestment] = useState(0);
  const [maxInvestment, setMaxInvestment] = useState(0);

  const storeOptions = stores.map((branch) => ({
    value: branch.id,
    titleLabel: `${branch.storename} (${branch.storecode})`,
    // subLabel: branch.address_line1,
    bodyLabel: `${branch.city} ${branch.state} ${branch.pincode}`,
  }));

  useEffect(() => {
    const fetchVariants = async () => {
      if (values.plan) {
        setLoadingVariants(true);
        setErrorVariants('');
        try {
          let response = await ActiveStoresApi.List({ category: values.plan });
          response = response.data;
          console.log('Response from variants', response.data.list[0].varient);
          setVariants(response.data.list[0].varient);
        } catch (error) {
          setErrorVariants('Failed to fetch variants');
        }
        setLoadingVariants(false);
      } else {
        setVariants([]);
      }
    };

    fetchVariants();
  }, [values.plan]);

  useEffect(() => {
    const fetchStores = async () => {
      if (values.variant) {
        setLoadingStores(true);
        setErrorStores('');
        try {
          const selectedVariant = variants.find(
            (variant) => variant.id === values.variant
          );
          console.log('Fetched Stores = ', selectedVariant.store);
          if (selectedVariant) {
            setStores(selectedVariant.store);
            setFieldValue('minInvestment', selectedVariant.min_investment);
            setFieldValue('maxInvestment', selectedVariant.max_investment);
            setMinInvestment(selectedVariant.min_investment);
            setMaxInvestment(selectedVariant.max_investment);
          } else {
            setStores([]);
            setMinInvestment(0);
            setMaxInvestment(0);
          }
        } catch (error) {
          setErrorStores('Failed to fetch Branches');
        }
        setLoadingStores(false);
      } else {
        setStores([]);
      }
    };

    fetchStores();
  }, [values.variant, variants]);

  return (
    <VStack spacing={4} align='stretch'>
      <SelectField
        label='Select Plan'
        name='plan'
        placeholder='Select Saving Plan'
        options={Categories.map((category) => ({
          label: category,
          value: category,
        }))}
        required
        onChange={(e) => {
          setFieldValue('plan', e.target.value);
          setFieldValue('variant', ''); // Reset variant when plan changes
          setFieldValue('Branch', ''); // Reset store when plan changes
          setFieldValue('amount', ''); // Reset amount when plan changes
        }}
      />
      {loadingVariants ? (
        <Spinner />
      ) : (
        <SelectField
          label='Select Plan Variant'
          name='variant'
          placeholder='Select Saving Variant'
          options={variants.map((variant) => ({
            value: variant.id,
            label: `${variant.period} Months`,
          }))}
          required
          onChange={(e) => {
            setFieldValue('variant', e.target.value);
            setFieldValue('Branch', ''); // Reset store when variant changes
            setFieldValue('amount', ''); // Reset amount when variant changes
          }}
        />
      )}
      {errorVariants && <Text color='red.500'>{errorVariants}</Text>}

      {values.variant && (
        <>
          {loadingStores ? (
            <Spinner />
          ) : (
            <MultiLineSelectField
              label='Select Branch'
              name='store'
              options={storeOptions}
            />
          )}
        </>
      )}

      {values.store && (
        <InputField
          label='Plan Amount'
          required={true}
          name='amount'
          type='number'
          min={minInvestment}
          max={maxInvestment}
          placeholder={`Enter amount between ${minInvestment} and ${maxInvestment}`}
        />
      )}
    </VStack>
  );
};

const Step2 = () => (
  <VStack spacing={4} align='stretch'>
    <Flex direction={{ base: 'column', md: 'row' }} gap={2}>
      <InputField
        label='Nominee Name'
        required={true}
        name='nomineeName'
        type='text'
      />

      <SelectField
        label='Nominee Relation'
        name='nomineeRelation'
        placeholder='Select Relation'
        options={[
          { value: 'Father', label: 'Father' },
          { value: 'Mother', label: 'Mother' },
          { value: 'Brother', label: 'Brother' },
          { value: 'Sister', label: 'Sister' },
          { value: 'Spouse', label: 'Spouse' },
          { value: 'Son', label: 'Son' },
          { value: 'Daughter', label: 'Daughter' },
          { value: 'Other', label: 'Other' },
        ]}
      />
    </Flex>

    <Flex align='center' direction={{ base: 'column', md: 'row' }} gap={2}>
      <InputField
        label='Nominee Contact Details'
        required={true}
        name='nomineeContact'
        type='number'
      />
      <InputField
        label='Nominee Date of Birth'
        required={true}
        name='nomineeDob'
        type='date'
        max={new Date().toISOString().split('T')[0]}
      />
      <InputField
        label='Aadhaar (optional)'
        name='nomineeAadhaar'
        type='text'
      />
    </Flex>
    <InputField
      label='Referral Code (Optional)'
      name='referralCode'
      placeholder='Enter referral code'
    />
  </VStack>
);

// const Step3 = ({ values, setStep, enrollmentId }) => {
//   const [orderSummary, setOrderSummary] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [acceptTerms, setAcceptTerms] = useState(false);

//   useEffect(() => {
//     console.log('enrollment id', enrollmentId, values);
//     const fetchOrderSummary = async () => {
//       setLoading(true);
//       setError('');
//       try {
//         const response = await CustomerSavingPlanApi.getById(enrollmentId);
//         setOrderSummary(response.data);
//       } catch (error) {
//         setError('Failed to fetch order summary');
//       }
//       setLoading(false);
//     };

//     fetchOrderSummary();
//   }, [enrollmentId]);

//   return (
//     <Card bgColor='gray.200' p={5}>
//       {loading ? (
//         <Spinner />
//       ) : error ? (
//         <Text color='red.500'>{error}</Text>
//       ) : (
//         <>
//           <Text fontSize='xl' fontWeight='700' align='center'>
//             Order Summary
//           </Text>
//           <Box>
//             <Text fontWeight='bold'>Plan Details</Text>
//             <Card bgColor='white' p={4} mb={4}>
//               <Text>Plan: {values.plan}</Text>
//               <Text>Variant: {values.variant}</Text>
//               <Text>Branch: {values.store}</Text>
//               <Text>Plan Amount: {values.amount}</Text>
//             </Card>
//             <Text fontWeight='bold'>Nominee Details</Text>
//             <Card bgColor='white' p={4} mb={4}>
//               <Text>Nominee Name: {values.nomineeName}</Text>
//               <Text>Nominee Relation: {values.nomineeRelation}</Text>
//               <Text>Nominee Contact: {values.nomineeContact}</Text>
//               <Text>Nominee Date of Birth: {values.nomineeDob}</Text>
//               <Text>Nominee Aadhaar: {values.nomineeAadhaar}</Text>
//             </Card>
//             <Card bgColor='white' p={4} mb={4}>
//               <Text fontWeight='bold'>Order Details</Text>
//               <Text>Order ID: {orderSummary.orderId}</Text>
//               <Text>Total Amount: {orderSummary.totalAmount}</Text>
//               <Text>Order Date: {orderSummary.orderDate}</Text>
//             </Card>
//           </Box>
//           <Flex>
//             <Checkbox
//               name='acceptTerms'
//               colorScheme='blue'
//               isChecked={acceptTerms}
//               onChange={(e) => setAcceptTerms(e.target.checked)}
//             >
//               I accept the terms and conditions
//             </Checkbox>
//           </Flex>
//           <Button
//             onClick={() => setStep(4)}
//             colorScheme='blue'
//             mt={4}
//             disabled={!acceptTerms}
//           >
//             Pay
//           </Button>
//         </>
//       )}
//     </Card>
//   );
// };

const handleNextStep1 = async (
  values,
  setFieldValue,
  setStep,
  setEnrollmentId,
  toast,
  state
) => {
  const { store, amount } = values;
  console.log('state', state);
  // if (!store || !amount) {
  //   toast({
  //     title: 'Please select a Branch and enter the amount.',
  //     status: 'error',
  //     duration: 5000,
  //     isClosable: true,
  //     position: 'top',
  //   });
  //   return;
  // }

  try {
    let response = await ActiveStoresApi.Enroll({
      customerId: state,
      storeId: store,
      monthlyAmount: amount,
    });
    response = response.data;
    console.log('Enrollment API response:', response);
    if (response.status_code === 200) {
      setEnrollmentId(response.data.id);
      setStep(2);
    } else {
      toast({
        title: response.message || 'Failed to enroll in the plan.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  } catch (error) {
    toast({
      title: error.response?.data?.message || 'Failed to enroll in the plan.',
      status: 'error',
      duration: 5000,
      isClosable: true,
      position: 'top',
    });
  }
};

const handleNextStep2 = async (
  values,
  setStep,
  enrollmentId,
  toast,
  state,
  setOrderSummary
) => {
  console.log('Enrollment id passed', enrollmentId);
  const {
    nomineeName,
    nomineeRelation,
    nomineeContact,
    nomineeDob,
    nomineeAadhaar,
  } = values;

  try {
    // Update enrollment
    let updateRes = await ActiveStoresApi.Update({
      enrollmentId,
      nomineeName,
      nomineeRelation,
      nomineeContact,
      nomineeDob,
      nomineeAadhaar,
      referralCode: values.referralCode,
    });
    if (updateRes.data.status_code !== 200) {
      toast({
        title: updateRes.data.message || 'Failed to update enrollment.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    let response = await ActiveStoresApi.CreateOrder({
      enrollmentId,
      customerId: state,
    });
    console.log('Response from order creation', response);
    if (response.data.status_code === 200) {
      setOrderSummary(response.data.data); // Set order summary state
      setStep(3);
    } else {
      toast({
        title: response.data.message || 'Failed to create order.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  } catch (error) {
    toast({
      title: error.response?.data?.message || 'Failed to update enrollment or create order.',
      status: 'error',
      duration: 5000,
      isClosable: true,
      position: 'top',
    });
  }
};

export default function SavingPlanEnrollmentForm(props) {
  const history = useHistory();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const { state } = useLocation();
  const [enrollmentId, setEnrollmentId] = useState();
  const [orderSummary, setOrderSummary] = useState();
  console.log('Customer id received', state);

  const initialValues = {
    plan: '',
    variant: '',
    store: '',
    amount: '',
    referralCode: '',
    nomineeName: '',
    nomineeRelation: '',
    nomineeContact: '',
    nomineeDob: '',
    nomineeAadhaar: '',
    acceptTerms: false,
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleNext = async (values, setFieldValue) => {
    if (step === 1) {
      await handleNextStep1(
        values,
        setFieldValue,
        setStep,
        setEnrollmentId,
        toast,
        state
      );
    } else if (step === 2) {
      await handleNextStep2(
        values,
        setStep,
        enrollmentId,
        toast,
        state,
        setOrderSummary
      );
    }
  };

  const handleSubmit = async (values, actions) => {
    // API call to save the data
    console.log(values);
    actions.setSubmitting(false);
    toast({
      title: 'Plan enrolled successfully.',
      status: 'success',
      duration: 5000,
      isClosable: true,
      position: 'top',
    });
    history.push('/plans/summary');
  };

  return (
    <Flex direction='column' mt={32} mx={5} maxWidth={'5xl'}>
      <Formik
        initialValues={initialValues}
        validationSchema={
          step === 1
            ? validationSchema.fields.step1
            : validationSchema.fields.step2
        }
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form noValidate>
            <Card bgColor='gray.200' p={5}>
              <Progress value={(step / 3) * 100} mb={4} width='100%' />

              {step === 1 && (
                <Step1 values={values} setFieldValue={setFieldValue} />
              )}
              {step === 2 && <Step2 />}
              {step === 3 && (
                <OrderSummaryPage
                  values={values}
                  setStep={setStep}
                  enrollmentId={enrollmentId}
                  orderSummary={orderSummary}
                // storeOptions={storeOptions}
                />
              )}

              <HStack mt={10} justify='space-between'>
                {step > 1 && (
                  <Button onClick={handleBack} isDisabled={isSubmitting}>
                    Back
                  </Button>
                )}
                {step < 3 && (
                  <Button
                    onClick={() => handleNext(values, setFieldValue)}
                    isDisabled={isSubmitting}
                  >
                    Next
                  </Button>
                )}
              </HStack>
            </Card>
          </Form>
        )}
      </Formik>
    </Flex>
  );
}
